import { useMeasure } from '@uidotdev/usehooks'
import { Pane } from '@criteria-labs/react'
import { useEffect, useRef } from 'react'
import { Tree, TreeApi } from 'react-arborist'
import { ProjectItem, isProjectFolder } from '../ProjectItem'
import { ProjectManager, useCurrentProjectItems } from '../ProjectManager'
import { useProjectSelection } from '../selection/useProjectSelection'
import { ProjectNavigatorItem } from './ProjectNavigatorItem'
import { ProjectNavigatorRow } from './ProjectNavigatorRow'

export function ProjectNavigator() {
  const currentProjectItems = useCurrentProjectItems()
  const { selection, selectionIsNew, select, deselect } = useProjectSelection()

  const treeRef = useRef<TreeApi<ProjectItem> | undefined>(undefined)

  useEffect(() => {
    if (selection && selectionIsNew) {
      setTimeout(() => {
        treeRef.current?.edit(selection)
      }, 200)

      select(selection, false)
    }
  }, [selection, selectionIsNew, treeRef, select])

  const [paneRef, { height: paneHeight }] = useMeasure()

  return (
    <Pane ref={paneRef}>
      <Tree
        ref={(instance) => {
          treeRef.current = instance ?? undefined
        }}
        // data
        data={currentProjectItems}
        idAccessor="path"
        childrenAccessor="items"
        // data handlers
        onCreate={({ parentId, index, type }) => {
          return { id: 'new' }
        }}
        onMove={({ dragIds, dragNodes, parentNode, index }) => {
          ProjectManager.shared().moveItemsSync(
            dragNodes.map((node) => node.data),
            parentNode && isProjectFolder(parentNode.data) ? parentNode.data : null
          )
        }}
        onRename={({ node, name }) => {
          try {
            ProjectManager.shared().renameItemSync(node.data, name, (renamedItem) => {
              // needs to allow time for file to be written asynchronously,
              // otherwise selection will revert to default
              setTimeout(() => {
                select(renamedItem.path, false)
              }, 100)
            })
          } catch (error) {
            console.error(error)
          }
        }}
        onDelete={({ nodes }) => {
          for (const node of nodes) {
            ProjectManager.shared().removeItemSync(node.data)
          }
        }}
        // renderers
        renderRow={ProjectNavigatorRow}
        // sizes
        rowHeight={24}
        width="100%"
        height={paneHeight ?? undefined}
        indent={24}
        padding={6}
        // config
        openByDefault={false}
        selectionFollowsFocus={true}
        disableMultiSelection={true}
        disableDrag={(item) => item.store === 'userFileSystem'}
        disableDrop={({ parentNode }) => parentNode.data.store === 'userFileSystem'}
        // event handlers
        onSelect={(nodes) => {
          nodes.length > 0 ? select(nodes[0].data.path, false) : deselect()
        }}
        // selection
        selection={selection ?? undefined}
      >
        {ProjectNavigatorItem}
      </Tree>
    </Pane>
  )
}
